<template>
  <div class="w-full md:max-w-4xl mx-auto md:p-4 p-2">
    <div v-if="loading" class="text-center p-2 md:p-4">
      <ProgressSpinner />
      <p class="mt-2 text-gray-600">Chargement des données...</p>
    </div>

    <!-- Section des vigilances (pas de conditionnel MyTournee) -->
    <div v-else-if="isFullyValidated" class="w-full">
      <!-- Comme cette section entière est maintenant affichée seulement temporairement
           avant la redirection, nous n'avons pas besoin d'inclure MyTournee ici -->
      
      <!-- Section des vigilances -->
      <div v-if="tourDetails.vigilances.length > 0" class="mt-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold text-gray-800">Points de vigilance de votre tournée</h2>
          <span class="text-sm text-gray-500">
            {{ tourDetails.vigilances.length }} point(s) à surveiller
          </span>
        </div>

        <div class="space-y-4">
          <div v-for="vigilance in tourDetails.vigilances" :key="vigilance.id"
            class="border rounded-lg bg-white shadow-sm">
            <div class="p-4">
              <!-- Informations de livraison -->
              <div v-for="delivery in vigilance.deliveries" :key="delivery.id" class="mb-3">
                <h3 class="text-base font-bold text-gray-800">
                  {{ delivery.deliveryLocation.name }}
                </h3>
                <p class="text-sm text-gray-600">
                  {{ delivery.deliveryLocation.address }}
                </p>
                <p class="text-sm text-gray-500">
                  {{ delivery.deliveryLocation.zip_code }} {{ delivery.deliveryLocation.city }}
                </p>
              </div>

              <!-- Détails de la vigilance -->
              <div class="bg-orange-50 rounded p-3 mt-3">
                <div class="flex items-start gap-2">
                  <i class="pi pi-exclamation-circle text-orange-500 mt-0.5"></i>
                  <div>
                    <span class="text-sm font-semibold text-orange-800 block">
                      {{ vigilance.title }}
                    </span>
                    <p class="text-sm text-gray-700 mt-1">
                      {{ vigilance.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="text-center p-2 md:p-4">
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4 mb-4">
        <i class="pi pi-exclamation-circle text-red-500 mr-2"></i>
        <span class="text-red-700">{{ error }}</span>
      </div>
      <Button label="Rafraichir" icon="pi pi-refresh" @click="fetchTourData" :disabled="isRefreshDisabled"
        class="mt-2 w-full md:w-auto" severity="secondary">
        <template #loading v-if="timeoutRemaining > 0">
          {{ timeoutRemaining }}s
        </template>
      </Button>
    </div>

    <div v-else-if="!tourDetails" class="text-center p-2 md:p-4">
      <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4 mb-4">
        <i class="pi pi-info-circle text-gray-500 mr-2"></i>
        <span class="text-gray-600">Aucune tournee a charger pour le moment</span>
      </div>
      <Button label="Rafraichir" icon="pi pi-refresh" @click="fetchTourData" :disabled="isRefreshDisabled"
        class="mt-2 w-full md:w-auto" severity="secondary">
        <template #loading v-if="timeoutRemaining > 0">
          {{ timeoutRemaining }}s
        </template>
      </Button>
    </div>

    <div v-else class="w-full">
      <ProductList v-if="currentStep === 'products'" :tour-details="tourDetails" @next-step="goToChecklist"
        class="w-full" />

      <CheckList v-if="currentStep === 'checklist'" :tour-details="tourDetails" @next-step="goToVigilances"
        @prev-step="goToProducts" class="w-full" />

      <VigilanceList v-if="currentStep === 'vigilances'" :tour-details="tourDetails" @next-step="goToConfirmation"
        @prev-step="goToChecklist" class="w-full" />

      <TourConfirmation v-if="currentStep === 'confirmation'" :tour-details="tourDetails"
        @validate-tour="handleTourValidation" @prev-step="goToChecklist" class="w-full" />
    </div>

    <Toast ref="toastRef" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router'; // Importer le router
import axios from '@/api/axios';
import ProductList from './ProductList.vue';
import CheckList from './CheckList.vue';
import TourConfirmation from './TourConfirmation.vue';
import VigilanceList from './VigilanceList.vue';

const TIMEOUT_DURATION = 10;

const router = useRouter(); // Initialiser le router
const toastRef = ref();
const loading = ref(true);
const error = ref(null);
const tourDetails = ref(null);
const currentStep = ref('products');
const timeoutRemaining = ref(0);
let timeoutInterval = null;

const isRefreshDisabled = computed(() => timeoutRemaining.value > 0);

// Vérifier si la tournée est complètement validée
const isFullyValidated = computed(() => {
  return tourDetails.value &&
    tourDetails.value.isPrepared &&
    tourDetails.value.isControled &&
    tourDetails.value.isLoaded;
});

// Surveiller les changements de isFullyValidated pour rediriger l'utilisateur
watch(isFullyValidated, (newValue) => {
  if (newValue === true) {
    // Rediriger vers la route MyTournee quand la tournée est validée
    router.push('/MyTournee');
  }
});

const startTimeout = () => {
  timeoutRemaining.value = TIMEOUT_DURATION;

  if (timeoutInterval) {
    clearInterval(timeoutInterval);
  }

  timeoutInterval = setInterval(() => {
    if (timeoutRemaining.value > 0) {
      timeoutRemaining.value--;
    } else {
      clearInterval(timeoutInterval);
    }
  }, 1000);
};

const fetchTourData = async () => {
  if (isRefreshDisabled.value) return;

  loading.value = true;
  error.value = null;

  try {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await axios.get(`admin-explo/tournees-livreurs/?start_date=${today}`);

    if (data && data.tour_products) {
      data.products = data.tour_products
        .filter(product => !product.package__name.includes('cageot'))
        .map(product => ({
          name: product.package__name,
          quantity: product.total_quantity,
          prepared: false
        }));
      tourDetails.value = data;
      
      // Vérifier immédiatement si nous devons rediriger
      if (isFullyValidated.value) {
        router.push('/MyTournee');
      }
    } else {
      tourDetails.value = null;
      startTimeout();
    }

  } catch (err) {
    tourDetails.value = null;

    if (err.response) {
      if (err.response.status === 404) {
        error.value = err.response.data.error || "Aucune tournee trouvee pour le moment.";
      } else {
        error.value = err.response.data.error || 'Une erreur est survenue lors du chargement des donnees';
      }
    } else if (err.request) {
      error.value = 'Le serveur ne repond pas. Veuillez reessayer plus tard.';
    } else {
      error.value = 'Une erreur est survenue lors de la communication avec le serveur';
    }
    console.error('Erreur lors de la recuperation des donnees:', err);
    startTimeout();
  } finally {
    loading.value = false;
  }
};

const goToProducts = () => currentStep.value = 'products';
const goToChecklist = () => currentStep.value = 'checklist';
const goToVigilances = () => currentStep.value = 'vigilances';
const goToConfirmation = () => currentStep.value = 'confirmation';

const handleTourValidation = async () => {
  try {
    const updatedTour = {
      id: tourDetails.value.id,
      isLoaded: true,
    };

    await axios.patch(`admin-explo/tournees/${updatedTour.id}/`, updatedTour);

    toastRef.value.add({
      severity: 'success',
      summary: 'Succes',
      detail: 'Tournee validee avec succes. Bon voyage !',
      life: 5000
    });

    await fetchTourData();
    // La redirection se fera automatiquement via le watcher si isFullyValidated devient true
    
  } catch (err) {
    const errorMessage = err.response?.data?.error || 'Erreur lors de la validation de la tournee';

    toastRef.value.add({
      severity: 'error',
      summary: 'Erreur',
      detail: errorMessage,
      life: 5000
    });
  }
};

onMounted(() => {
  fetchTourData();
});

onUnmounted(() => {
  if (timeoutInterval) {
    clearInterval(timeoutInterval);
  }
});
</script>